import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import {
  type AuthorizeParams,
  type ConsentMetadata,
  getConsentMetadata,
  grantConsent,
} from '@/api/oauthClients';
import { errorDetail } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';

/**
 * OAuth 2.0 consent / approval screen (epic 200).
 *
 * Standalone (no AppShell chrome): reads the in-flight authorization-code
 * params from its own query string, fetches the consent metadata, renders the
 * requesting app + the scopes it is asking for, and on Approve/Deny posts the
 * grant decision then redirects to the URL the backend returns (the client's
 * redirect_uri carrying ?code=&state= or ?error=). Lives under
 * <ProtectedRoute>, so an unauthenticated visitor is bounced to /login with
 * returnTo pointing back here.
 */
export function OAuthConsentPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();

  const authParams: AuthorizeParams = {
    clientId: params.get('client_id') ?? '',
    redirectUri: params.get('redirect_uri') ?? '',
    scope: params.get('scope') ?? '',
    state: params.get('state') ?? '',
    codeChallenge: params.get('code_challenge') ?? '',
    codeChallengeMethod: params.get('code_challenge_method') ?? '',
  };

  const [meta, setMeta] = useState<ConsentMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getConsentMetadata(authParams)
      .then(setMeta)
      .catch((err) => setError(errorDetail(err)))
      .finally(() => setLoading(false));
    // The OAuth params are fixed for the lifetime of this page render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function decide(approve: boolean) {
    if (!meta) return;
    setSubmitting(true);
    setError(null);
    try {
      const { redirectUrl } = await grantConsent({
        clientId: meta.clientId,
        redirectUri: meta.redirectUri,
        scope: authParams.scope,
        state: meta.state,
        codeChallenge: authParams.codeChallenge,
        codeChallengeMethod: authParams.codeChallengeMethod,
        approve,
      });
      window.location.assign(redirectUrl);
    } catch (err) {
      setError(errorDetail(err));
      setSubmitting(false);
    }
  }

  const scopeList = meta?.scopes ?? [];

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-lop-bg-deep)] p-4">
      <div className="w-full max-w-md rounded-[10px] border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)] p-8 shadow-[var(--shadow-lop-md)]">
        {loading ? (
          <p className="text-center text-sm text-[var(--color-lop-text-secondary)]">
            {t('common.loading')}
          </p>
        ) : error && !meta ? (
          <div className="text-center">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : meta ? (
          <>
            <div className="mb-6 flex flex-col items-center text-center">
              {meta.logoUrl ? (
                <img
                  src={meta.logoUrl}
                  alt={meta.appName}
                  className="mb-3 h-16 w-16 rounded-xl object-contain"
                />
              ) : (
                <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-xl bg-[var(--color-lop-bg-card-hi)]">
                  <ShieldCheck className="h-8 w-8 text-[var(--color-lop-teal)]" />
                </div>
              )}
              <h1 className="text-xl font-semibold text-[var(--color-lop-text-primary)]">
                {t('oauthConsent.heading', { app: meta.appName })}
              </h1>
              <p className="mt-1 text-sm text-[var(--color-lop-text-secondary)]">
                {t('oauthConsent.subtitle', { app: meta.appName })}
              </p>
            </div>

            <div className="mb-6">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-lop-text-muted)]">
                {t('oauthConsent.scopesTitle')}
              </p>
              {scopeList.length === 0 ? (
                <p className="text-sm text-[var(--color-lop-text-secondary)]">
                  {t('oauthConsent.noScopes')}
                </p>
              ) : (
                <ul className="space-y-2">
                  {scopeList.map((scope) => (
                    <li
                      key={scope}
                      className="flex items-start gap-2 rounded-md border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card-hi)] px-3 py-2"
                    >
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-lop-teal)]" />
                      <div className="min-w-0">
                        <p className="text-sm text-[var(--color-lop-text-primary)]">
                          {t(`oauthConsent.scope.${scope}`, {
                            defaultValue: t('oauthConsent.scopeGeneric', { scope }),
                          })}
                        </p>
                        <code className="font-mono text-[10px] text-[var(--color-lop-text-muted)]">
                          {scope}
                        </code>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {error && (
              <div className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                disabled={submitting}
                onClick={() => decide(false)}
                data-testid="oauth-consent-deny"
              >
                {t('oauthConsent.deny')}
              </Button>
              <Button
                className="flex-1"
                disabled={submitting}
                onClick={() => decide(true)}
                data-testid="oauth-consent-approve"
              >
                {submitting ? t('common.loading') : t('oauthConsent.approve')}
              </Button>
            </div>

            <p className="mt-4 text-center text-[11px] text-[var(--color-lop-text-muted)]">
              {t('oauthConsent.redirectNote', { uri: meta.redirectUri })}
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
